import _ from 'lodash'
import { useCallback, useEffect, useState } from 'react'

/** API */
import PatientManagementApi from 'api/counter/patient-management/patient-management.api'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** UTILS || SLICE */
import { UseSnCode } from 'utils/useSnCode'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Grid, InputAdornment } from '@mui/material'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'


/** STYLE */

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

export default function PatientManagement() {
  const { t } = useTranslation()

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('DESC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [patients, setPatients] = useState([])
  const [isChecks, setIsChecks] = useState([] as any)
  const [isCheckAll, setIsCheckAll] = useState('')
  const [snType, setSnType] = useState(false);
  const [fetch, setFetch] = useState<boolean>(false);

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [idAll, setIdAll] = useState<any>([])
  const [checkAll, setCheckAll] = useState<any>([])
  const [name, setName] = useState('')
  const [configModal, setConfigModal]: any = useState({ title: '', message: '', size: '', modul: '', onReset: null, onSubmit: null, textBtnCancel: '', textBtnConfirm: null })


  const handleSnCode = useCallback(() => {
    const fetchData = async () => {
      const sn = await UseSnCode();
      setSnType(sn);
    };

    fetchData();

  }, [snType]);

  useEffect(() => {
    handleSnCode()
  }, [])


  const loadData = useCallback(async () => {
    try {
      let condition: any = {}
      if (page) condition = { ...condition, page: page }
      if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
      if (search) condition = { ...condition, search: search }
      if (sortBy) condition = { ...condition, sortBy: sortBy }
      if (sortType) condition = { ...condition, sortType: sortType }
      if (status) condition = { ...condition, status: status === 'ALL' ? '' : status } //fix filter status all
      const patient = await PatientManagementApi.findAll(condition)
      if (patient.status === 200) {
        setRowCount(patient.headers['x-total'])
        setPatients(patient.data)
      }
    } finally {
      setFetch(false);
    }

  }, [page, pageLimit, sortBy, sortType, fetch])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const onActive = () => {
    if (id) {
      PatientManagementApi.updateStatus(id, statusAlertModal)
        .then((resp) => {
          setIsOpenAlertModal(false)
          notiSuccess(t('PATIENT_MANAGEMENT.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        })
        .catch((e) => {
          setIsOpenAlertModal(false)
          notiError(t('PATIENT_MANAGEMENT.MESSAGE.ERROR'))
        })
    }
  }


  const onActiveAll = (id: Array<number>, checkboxStatus: string) => {
    PatientManagementApi.updateStatusByCheckbox(id, checkboxStatus)
      .then((resp) => {
        setIsOpenAlertModal(false)
        notiSuccess(t('PATIENT_MANAGEMENT.MESSAGE.SUCCESS.UPDATE_STATUS'))
        loadData()
      })
      .catch((e) => {
        setIsOpenAlertModal(false)
        notiError(t('PATIENT_MANAGEMENT.MESSAGE.ERROR'))
      })
      .finally(() => {
        setIsOpenAlertModal(false)
        setIsChecks([])
        setIdAll([])
        setCheckAll([])
      })
  }



  const onActiveByCheckbox = (id: Array<number>, checkboxStatus: string) => {
    setIsOpenAlertModal(true)
    setIdAll(id)
    setCheckAll(checkboxStatus)
  }

  const onCheckAll = (event: any) => {
    const { checked } = event
    const id = _.map(patients, (i: any) => {
      return i.patientId
    })
    if (checked) {
      setIsChecks(Array.from(new Set([...isChecks, ...id])))
    } else {
      setIsChecks(_.filter(isChecks, (i) => !_.includes(id, i)))
    }
  }

  const onCheck = (event: any) => {
    const { checked, value } = event
    if (checked) {
      setIsChecks([...isChecks, Number(value)])
    } else {
      setIsChecks(
        _.filter(isChecks, (i: number) => {
          return i !== Number(value)
        })
      )
    }
  }

  const handleOnCheckAll = () => {
    const hasSome = _.some(patients, (i: any) => _.some(isChecks, (j: number) => Number(i.patientId) === Number(j)))
    const hasAll = _.every(patients, (i: any) => _.some(isChecks, (j: number) => Number(i.patientId) === Number(j)))
    if (!_.isEmpty(isChecks) && !hasAll && hasSome) setIsCheckAll('indeterminate')
    else if (!_.isEmpty(isChecks) && hasAll) setIsCheckAll('checked')
    else setIsCheckAll('')
  }

  useEffect(() => {
    handleOnCheckAll()
  }, [isChecks, patients])

  const headCells = [
    {
      id: 'patientSelect',
      disablePadding: false,
      label: 'CHECKBOX',
      values: [
        { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`) },
        { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`) }
      ],
      indeterminate: isCheckAll === 'indeterminate' && true,
      checked: isCheckAll === 'checked' && true,
      disableDropdown: _.isEmpty(isChecks),
      onCheckAll: (event: any) => onCheckAll(event.target),
      onActive: () => onActiveByCheckbox(isChecks, 'ACTIVE'),
      onInactive: () => onActiveByCheckbox(isChecks, 'INACTIVE')
    },
    { id: 'no', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.NO') },
    { id: 'cnNumber', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.PATIENT_ID') },
    { id: 'branchId', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.BRANCH_ID') },
    { id: 'snCode', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.SN_CODE') },
    { id: 'patientName', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.NAME') },
    { id: 'idCard', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.ID_CARD') },
    { id: 'phone', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.PHONE_NUMBER') },
    { id: 'status', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.LAST_UPDATE') },
    { id: 'action', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.ACTION') }
  ]
  if (!snType) {
    headCells.splice(4, 1)
  }
  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1

    const { patientId, cnNumber, branchCnNumber, snCode, firstname, lastname, idCard, phone, createdBy, updatedBy, updatedAt, status: patientStatus } = objData
    const statusBtnActive = patientStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = patientStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const rowCheck = _.some(isChecks, (i: number) => Number(i) === Number(patientId))
    const fullName = `${firstname} ${lastname}`
    const objRenderData = {
      key: patientId,
      id: patientId,
      obj: objData,
      rowSelect: rowCheck,
      columns: [
        { option: 'CHECKBOX', align: 'center', label: patientId },
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: cnNumber ?? '-' },
        { option: 'TEXT', align: 'left', label: branchCnNumber ?? '-' },
        { option: 'TEXT', align: 'left', label: snCode ?? '-' },
        { option: 'TEXT', align: 'left', label: fullName ?? '-' },
        { option: 'TEXT', align: 'left', label: idCard ? idCard : '-' },
        { option: 'TEXT', align: 'left', label: phone ?? '-' },
        { option: 'STATUS', align: 'center', label: patientStatus },
        {
          option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy ?? createdBy, updatedAt: updatedAt }
        },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive }
          ]
        }
      ]
    }
    if (!snType) {
      objRenderData.columns.splice(4, 1)
    }

    return <TableRowCommon
      {...objRenderData}
      onactive={() => handleSubmitModal(Number(objRenderData.id), { fullName: fullName }, 'ACTIVE')}
      oninactive={() => handleSubmitModal(Number(objRenderData.id), { fullName: fullName }, 'INACTIVE')}
      onCheck={(event) => onCheck(event.target)}
    />
  }


  const handleSubmitModal = (id: number, value: { fullName: string }, status: string) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(`${value.fullName ? value.fullName : '-'}`)
  }
  return (
    <>
      <Box sx={{ height: '100%' }}>
        <AlertModal
          isOpen={isOpenAlertModal}
          title={t(`APPLICATION.MESSAGE.ALERT.CONFIRM_STATUS`)}
          message={idAll.length > 0 ? `${t(`PATIENT_MANAGEMENT.ALERT.CONFIRM_STATUS_PATIENTS`, { length: isChecks.length })}` : t(`PATIENT_MANAGEMENT.ALERT.CONFIRM_STATUS_PATIENT`, { fullName: name, status: t(`STATUS.${statusAlertModal}`) })}
          type={'warning'}
          handleClose={() => setIsOpenAlertModal(false)}
          onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
          onSubmit={{ onClick: () => idAll.length > 0 ? onActiveAll(idAll, checkAll) : onActive(), title: t('BUTTON.SUBMIT') }}
        />
        <HeaderCard text={t('PATIENT_MANAGEMENT.TITLE')} />
        <Box sx={{ px: 3, pt: 2 }}>
          <Grid container spacing={2} >
            <Grid item xs={12} sm={6} md={3} xl={2}>
              <TextInput
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                label={t('PATIENT_MANAGEMENT.SEARCH')}
                value={search}
                inputProps={{
                  endAdornment: (
                    <InputAdornment position="end" >
                      {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                        <FontAwesomeIcon
                          icon={faTimes}
                          onClick={() => {
                            setSearch('');
                          }}
                          style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid xs={12} sm={6} item md={3} xl={2}>
              <SelectInput
                label={t('STATUS.LABEL')}
                value={status}
                onChange={(event) => {
                  const value = event.target.value
                  if (value) {
                    handleChangeStatus(value)
                    setPage(1)
                  }
                }}
                options={[
                  { value: 'ALL', name: t('STATUS.ALL') },
                  { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                  { value: 'INACTIVE', name: t('STATUS.INACTIVE') },
                ]}
                width="100%"
              />
            </Grid>
            <Grid item xs={12} md={1} xl={1}>
              <MainButton
                title={t('INPUT.SEARCH')}
                type="primary"
                variant="contained"
                onClick={() => {
                  setFetch(true);
                  setPage(1)
                }}
                style={{ width: '120px' }}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, px: { xs: 1 }, pb: 4 }}>
            {!snType ? (
              <TableCustom
                page={page}
                pageLimit={pageLimit}
                sortType={sortType}
                sortBy={sortBy}
                rowCount={rowCount}
                onSort={onRequestSort}
                setPageLimit={handleChangeRowsPerPage}
                setPage={handleChangePage}
                headCells={headCells}
                rowsData={_.map(patients, (val, i) => {
                  return renderData(val, i)
                })}
              />
            ) :
              (
                <TableCustom
                  page={page}
                  pageLimit={pageLimit}
                  sortType={sortType}
                  sortBy={sortBy}
                  rowCount={rowCount}
                  onSort={onRequestSort}
                  setPageLimit={handleChangeRowsPerPage}
                  setPage={handleChangePage}
                  headCells={headCells}
                  tooltip={'UPDATE_BY'}
                  rowsData={_.map(patients, (val, i) => {
                    return renderData(val, i)
                  })}
                />
              )}
          </Box>
        </Box>
      </Box>
    </>
  )
}